import {
  Input,
  DatePicker,
  Tooltip,
  Table,
  Tabs,
} from "antd";
import type { TabsProps } from "antd";
import { ColumnProps } from "antd/lib/table";
import React, { Fragment, useState, useCallback, useEffect } from "react";
import { IUser } from "../users/models/IUser";
import axios from "axios";
import dayjs from "dayjs";
import { formatDateWithTimezoneOffset } from "../../lib/lib";
import StableDatePicker from "../common/StableDatePicker";
const { Search } = Input;

type StarType = "BUG" | "IDEA";

const BugHunterStatisticsPage: React.FC<{ endpoint?: string }> = ({ endpoint: propEndpoint }) => {
  const baseEndpoint = propEndpoint || `/api/public/bug-hunter-statistics`;

  const [activeTab, setActiveTab] = useState<StarType>("BUG");
  const [searchQuery, setSearchQuery] = useState("");

  const currentDate = new Date();
  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const lastDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  );
  const adjustedfirstDayOfMonth = formatDateWithTimezoneOffset(
    firstDayOfMonth,
    7
  );
  const adjustedlastDayOfMonth = formatDateWithTimezoneOffset(
    lastDayOfMonth,
    7
  );
  const [startDate, setStartDate] = useState<string | null>(
    adjustedfirstDayOfMonth
  );
  const [endDate, setEndDate] = useState<string | null>(adjustedlastDayOfMonth);

  const [totals, setTotals] = useState({
    starKudosGranted: 0,
    starTransfers: 0
  });

  // State để track current page và page size
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const columns: Array<ColumnProps<IUser>> = [
    {
      dataIndex: "realName",
      key: "realName",
      title: "Tên",
      render: (text: string, record: IUser, index: number) => {
        // Tính toán index thực tế trong toàn bộ danh sách
        const realIndex = (currentPage - 1) * pageSize + index;
        
        const getBadge = (realIndex: number) => {
          switch (realIndex) {
            case 0:
              return (
                <span 
                  style={{ 
                    fontSize: '20px', 
                    marginRight: '8px',
                    animation: 'bling 2s ease-in-out infinite',
                    display: 'inline-block'
                  }}
                >
                  🥇
                </span>
              ); // Gold medal with bling effect
            case 1:
              return <span style={{ fontSize: '20px', marginRight: '8px' }}>🥈</span>; // Silver medal
            case 2:
              return <span style={{ fontSize: '20px', marginRight: '8px' }}>🥉</span>; // Bronze medal
            default:
              return null; // No badge for other ranks
          }
        };

        return (
          <Tooltip title={record.realName}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {getBadge(realIndex)}
              {realIndex >= 3 && (
                <span style={{
                  fontSize: '14px',
                  color: '#8c8c8c',
                  fontWeight: 'normal',
                  marginRight: '8px',
                  minWidth: '20px'
                }}>
                  {realIndex + 1}.
                </span>
              )}
              <span 
                style={{ 
                  fontWeight: realIndex < 3 ? 'bold' : 'normal',
                  color: realIndex === 0 ? '#FFD700' : 'inherit', // Gold color for top 1
                  fontSize: realIndex === 0 ? '16px' : '14px' // Larger font for top 1
                }}
              >
                {record.realName}
              </span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: "starKudosGranted",
      key: "starKudosGranted",
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Sao Nhận</span>
          <span style={{ 
            fontSize: '12px', 
            color: '#1890ff', 
            fontWeight: 'bold',
            background: '#e6f7ff',
            padding: '2px 6px',
            borderRadius: '4px',
            border: '1px solid #91d5ff'
          }}>
            {totals.starKudosGranted.toLocaleString()}
          </span>
        </div>
      ),
      render: (value: number, record: IUser, index: number) => {
        const realIndex = (currentPage - 1) * pageSize + index;
        
        const getColor = (realIndex: number) => {
          switch (realIndex) {
            case 0:
              return "#FFD700"; // Brighter Gold
            case 1:
              return "#c0c0c0"; // Silver
            case 2:
              return "#cd7f32"; // Bronze
            default:
              return "#1890ff"; // Default blue
          }
        };

        return (
          <span style={{ 
            color: getColor(realIndex), 
            fontWeight: "bold",
            fontSize: realIndex < 3 ? "16px" : "14px"
          }}>
            {value?.toLocaleString() || 0}
          </span>
        );
      },
    },
    {
      dataIndex: "starTransfers",
      key: "starTransfers",
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Số Ghi Nhận</span>
          <span style={{ 
            fontSize: '12px', 
            color: '#fa8c16', 
            fontWeight: 'bold',
            background: '#fff7e6',
            padding: '2px 6px',
            borderRadius: '4px',
            border: '1px solid #ffd591'
          }}>
            {totals.starTransfers.toLocaleString()}
          </span>
        </div>
      ),
      render: (value: number, record: IUser, index: number) => {
        const realIndex = (currentPage - 1) * pageSize + index;
        
        const getColor = (realIndex: number) => {
          switch (realIndex) {
            case 0:
              return "#FFD700"; // Brighter Gold
            case 1:
              return "#c0c0c0"; // Silver
            case 2:
              return "#cd7f32"; // Bronze
            default:
              return "#fa8c16"; // Default orange
          }
        };

        return (
          <span style={{ 
            color: getColor(realIndex), 
            fontWeight: "bold",
            fontSize: realIndex < 3 ? "16px" : "14px"
          }}>
            {value?.toLocaleString() || 0}
          </span>
        );
      },
    },
  ];

  const handleDataLoaded = (response: any) => {
    if (response) {
      if (response.totals) {
        setTotals({
          starKudosGranted: response.totals.starKudosGranted ?? 0,
          starTransfers: response.totals.starTransfers ?? 0
        });
      }
      if (response.docs) setData(response.docs);
      if (response.totalDocs != null) setTotal(response.totalDocs);
    }
  };

  const handlePageChange = (page: number) => setCurrentPage(page);

  const tabItems: TabsProps["items"] = [
    { key: "BUG", label: "Sao BUG" },
    { key: "IDEA", label: "Sao IDEA" }
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('type', activeTab);
        if (searchQuery) params.append('search', searchQuery);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        params.append('page', currentPage.toString());
        params.append('limit', pageSize.toString());

        const response = await axios.get(`${baseEndpoint}?${params.toString()}`);
        handleDataLoaded(response.data);
      } catch (error) {
        console.error('Error fetching star statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeTab, currentPage, pageSize, searchQuery, startDate, endDate]);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to page 1 when searching
  }, []);

  const handleDateChange = useCallback((dates: [dayjs.Dayjs, dayjs.Dayjs] | null, dateStrings: [string, string]) => {
    if (dates && dateStrings) {
      setStartDate(dateStrings[0] ? formatDateWithTimezoneOffset(dates[0].toDate(), 7) : null);
      setEndDate(dateStrings[1] ? formatDateWithTimezoneOffset(dates[1].toDate(), 7) : null);
      setCurrentPage(1); // Reset to page 1 when changing date
    }
  }, []);


  return (
    <Fragment>
      <div style={{ 
        padding: '24px',
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
      }}>
        <Tabs
          activeKey={activeTab}
          onChange={(k) => { setActiveTab(k as StarType); setCurrentPage(1); }}
          items={tabItems}
          style={{ marginBottom: 16 }}
        />
        <div style={{ 
          display: 'flex', 
          gap: '16px', 
          marginBottom: '24px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <Search
            placeholder="Tìm kiếm theo tên..."
            allowClear
            style={{ width: 300 }}
            onSearch={handleSearch}
          />
          <StableDatePicker
            value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null}
            onChange={handleDateChange}
            format="DD/MM/YYYY"
            placeholder={['Từ ngày', 'Đến ngày']}
          />
        </div>

        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} mục`,
            onChange: handlePageChange,
            onShowSizeChange: (current, size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
            pageSizeOptions: ['10', '25', '50', '100'],
            defaultPageSize: 50,
          }}
          rowKey="userId"
        />
      </div>
    </Fragment>
  );
};

export default BugHunterStatisticsPage;
